import React, { useState, useEffect } from "react";
import { BatchAddStakeForm } from "@/components/BatchAddStakeForm";
import {
  useAccount,
  useSwitchChain,
  useWriteContract,
  useBalance,
  usePublicClient,
} from "wagmi";
import { parseUnits } from "ethers";
import { encodeFunctionData, parseEventLogs } from "viem";
import { useNotify } from "@/components/NotificationProvider";
import { useTransactionReceipt } from "@/hooks/useTransactionReceipt";
import { STAKING_V2_ABI } from "@/const/abi";
import { STAKING_V2_ADDRESS } from "@/const/address";
import { getFailedType } from "@/utils/errors";
import style from "./index.module.less";
import { formatBalance } from "@/utils/formatters";

interface BatchStakeData {
  netuid: number;
  amount: string;
  slippage: string;
}

function AddStake() {
  const { chainId: connectedChainId, address } = useAccount();
  const {
    data: balanceData,
    isError: balanceIsError,
    error: balanceError,
  } = useBalance({
    address,
    chainId: 964, // Subtensor EVM chain ID
  });
  console.log(
    "Balance data:",
    balanceData,
    "Formatted balance:",
    balanceData ? formatBalance(Number(balanceData.value) / 1e18) : "No data",
    "Balance error:",
    balanceIsError ? balanceError : "No error",
    "Connected chain:",
    connectedChainId,
    "Wallet address:",
    address
  );
  const { switchChain } = useSwitchChain();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const publicClient = usePublicClient();

  // Contract interaction hooks
  const {
    data: hash,
    writeContractAsync,
    isPending,
    isError: writeContractIsError,
    error: writeError,
  } = useWriteContract();

  const {
    isLoading: isConfirming,
    isSuccess,
    isError,
    data: receipt,
  } = useTransactionReceipt(hash, {
    enabled: !!loading,
    retryDelay: 5000,
    maxRetries: 10,
  });

  // Handle transaction status
  useEffect(() => {
    if (isSuccess && receipt) {
      setLoading(false);
      console.log("Batch add stake transaction confirmed:", receipt.logs);

      // Parse MulticallExecuted event to check actual execution results
      try {
        const logs = parseEventLogs({
          abi: STAKING_V2_ABI,
          logs: receipt.logs,
          eventName: "MulticallExecuted",
        });

        if (logs.length > 0) {
          const multicallEvent = logs[0] as any;
          const { callCount, successCount } = multicallEvent.args as {
            caller: string;
            callCount: bigint;
            successCount: bigint;
          };

          console.log("Multicall execution results:", {
            totalCalls: Number(callCount),
            successfulCalls: Number(successCount),
            failedCalls: Number(callCount) - Number(successCount),
          });

          if (Number(successCount) === Number(callCount)) {
            notify.success(
              "Batch stakes added successfully",
              `All ${Number(callCount)} stakes have been added successfully.`
            );
          } else {
            notify.warning(
              "Partial batch execution",
              `${Number(successCount)} out of ${Number(
                callCount
              )} stakes were added successfully.`
            );
          }
        } else {
          notify.success(
            "Batch stakes added successfully",
            "All your stakes have been added successfully in one transaction."
          );
        }
      } catch (error) {
        console.error("Error parsing MulticallExecuted event:", error);
        notify.success(
          "Batch stakes added successfully",
          "All your stakes have been added successfully in one transaction."
        );
      }
    }
  }, [isSuccess, receipt, notify]);

  useEffect(() => {
    if (isError || writeContractIsError) {
      setLoading(false);
      const errorMsg = writeError?.message || "Transaction failed";
      const failureType = getFailedType(
        (errorMsg || String(errorMsg)).toLowerCase()
      );
      notify.error("Add stake failed", failureType);
    }
  }, [isError, writeContractIsError, writeError, notify]);

  const handleSubmit = async (batchData: BatchStakeData[]) => {
    if (!batchData || batchData.length === 0) {
      notify.error("Validation Error", "Please add at least one stake");
      return;
    }
    console.log("Submitting batch stake data:", batchData);

    // Validate all batch items
    for (const item of batchData) {
      if (!item.amount || parseFloat(item.amount) <= 0) {
        notify.error(
          "Validation Error",
          "Please enter valid amounts for all stakes"
        );
        return;
      }
      if (!item.netuid) {
        notify.error("Validation Error", "Please select netuid for all stakes");
        return;
      }
    }

    setLoading(true);

    // Auto switch to correct chain if needed (assuming chain ID 964 for Subtensor EVM)
    if (connectedChainId && connectedChainId !== 964) {
      try {
        await switchChain({ chainId: 964 });
      } catch (error) {
        setLoading(false);
        notify.error(
          "Chain Switch Failed",
          "Please manually switch to Subtensor EVM network"
        );
        return;
      }
    }

    try {
      // Encode each addStake call
      const encodedCalls: string[] = [];

      for (const item of batchData) {
        const taoAmount = parseUnits(String(item.amount), 9);
        const netuid = BigInt(item.netuid);
        const slippage = BigInt(Math.floor((Number(item.slippage) || 1) * 10)); // Convert percentage to basis points
        const allowPartial = false; // Allow partial fulfillment

        // Encode the addStake function call
        const encodedCall = encodeFunctionData({
          abi: STAKING_V2_ABI,
          functionName: "addStake",
          args: [taoAmount, netuid, slippage, allowPartial],
        });

        encodedCalls.push(encodedCall);
      }

      console.log("Calling multicall with batch data:", {
        batchCount: batchData.length,
        batchData,
        encodedCalls,
      });

      // Dynamic gas estimation with fallback for multicall
      let gasLimit = BigInt(200000 + batchData.length * 200000); // Dynamic base: 200k + 200k per operation
      // try {
      //   if (publicClient) {
      //     const estimatedGas = await publicClient.estimateContractGas({
      //       address: STAKING_V2_ADDRESS,
      //       abi: STAKING_V2_ABI,
      //       functionName: "multicall",
      //       args: [encodedCalls],
      //     });
      //     console.log("Estimated Gas for multicall:", estimatedGas);
      //     console.log("Fallback gas would be:", gasLimit);
      //     gasLimit = BigInt(Math.floor(Number(estimatedGas) * 1.2) + 200000); // Add 20% buffer
      //   }
      // } catch (gasEstimateError) {
      //   console.log(
      //     "Multicall gas estimation failed, using fallback:",
      //     gasEstimateError
      //   );
      // }

      console.log("Final gas limit for multicall:", gasLimit);

      // Call the multicall contract function with dynamic gas estimation
      await writeContractAsync({
        address: STAKING_V2_ADDRESS,
        abi: STAKING_V2_ABI,
        functionName: "multicall",
        args: [encodedCalls],
        gas: gasLimit,
      });

      // Transaction submitted, waiting for confirmation in useEffect
    } catch (error: any) {
      setLoading(false);
      const failureType = getFailedType(
        (error?.message || String(error)).toLowerCase()
      );
      notify.error(
        "Batch add stake failed",
        `${failureType}. Please try again.`
      );
      console.error("Batch add stake error:", error);
    }
  };

  return (
    <div className={style.stake}>
      <div className={style.stake_title}>
        Batch Add Stakes: {formatBalance(Number(balanceData?.value) / 1e18)}t
      </div>
      <div className={style.stake_desc}>
        Add multiple stakes in one transaction - save on gas fees!
      </div>
      <BatchAddStakeForm
        onSubmit={handleSubmit}
        loading={loading}
        maxBalance={Number(balanceData?.value) / 1e18}
      />
    </div>
  );
}

export default AddStake;
